import * as THREE from "three";

// Camera is NOT parented to the aircraft — it reads the gunner pod world position
// and aircraft heading each frame, then builds its own world rotation with zero bank.
// This prevents the aircraft roll from tilting the view and keeps orbital motion readable.

export type CameraRigConfig = {
  fov: number;
  baseYaw: number;
  basePitch: number;
  yawClamp: number;
  pitchClamp: number;
  sensitivity: number;
};

export const defaultCameraRigConfig: CameraRigConfig = {
  fov: 55,
  baseYaw: 0,
  basePitch: -Math.PI / 4.5,
  yawClamp: (40 * Math.PI) / 180,
  pitchClamp: (45 * Math.PI) / 180,
  sensitivity: 0.0025,
};

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  private yaw = 0;
  private pitch = 0;
  private targetYaw = 0;
  private targetPitch = 0;

  constructor(
    scene: THREE.Scene,
    private readonly cfg: CameraRigConfig = defaultCameraRigConfig,
  ) {
    this.camera = new THREE.PerspectiveCamera(cfg.fov, 1, 0.5, 5000);
    this.camera.layers.set(0); // never renders aircraft (layer 1)
    scene.add(this.camera);
  }

  aim(mouseDX: number, mouseDY: number): void {
    this.targetYaw   = clamp(this.targetYaw   - mouseDX * this.cfg.sensitivity, -this.cfg.yawClamp,   this.cfg.yawClamp);
    this.targetPitch = clamp(this.targetPitch - mouseDY * this.cfg.sensitivity, -this.cfg.pitchClamp, this.cfg.pitchClamp);
  }

  // Called each frame with the pod's world position and the aircraft heading.
  // Never inherits aircraft roll — camera z-rotation is always 0.
  // dt drives the inertia lerp so lag is frame-rate independent.
  updateTransform(podWorldPos: THREE.Vector3, aircraftHeading: number, shakeOffset: THREE.Vector3, dt: number): void {
    // Exponential smoothing: tau ≈ 0.055s → snappy but weighted, not instant
    const lag = 1 - Math.pow(0.04, dt);
    this.yaw   += (this.targetYaw   - this.yaw)   * lag;
    this.pitch += (this.targetPitch - this.pitch) * lag;

    this.camera.position.copy(podWorldPos).add(shakeOffset);
    this.camera.rotation.order = "YXZ";
    this.camera.rotation.y = aircraftHeading + this.cfg.baseYaw + this.yaw;
    this.camera.rotation.x = this.cfg.basePitch + this.pitch;
    this.camera.rotation.z = 0;
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.fov = this.cfg.fov;
    this.camera.updateProjectionMatrix();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
